// hooks/useMapControls.js
import { useState, useRef, useCallback, useEffect } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { ROUTE_CONFIG } from '../config/routeConfig';
import { calculateBounds, calculateCenter, adjustCenterForOffset, extractCoordinates } from '../utils/geoUtils';

export const useMapControls = ({ 
  initialCenter = ROUTE_CONFIG.MAP.DEFAULT_CENTER,
  initialZoom = ROUTE_CONFIG.MAP.DEFAULT_ZOOM 
}) => {
  // Google Maps loading
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GMAPKEY
  });
  
  // Map state
  const [center, setCenter] = useState(initialCenter);
  const [zoomLevel, setZoomLevel] = useState(initialZoom);
  const mapRef = useRef(null);
  
  // Handle map load
  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);
  
  // Update map center
  const updateMapCenter = useCallback((newCenter, options = {}) => {
    const { animate = true, offset = 0 } = options;
    
    const adjustedCenter = offset ? 
      adjustCenterForOffset(newCenter, offset) : 
      newCenter;
    
    setCenter(adjustedCenter);
    
    if (mapRef.current && animate) {
      mapRef.current.panTo(adjustedCenter);
    }
  }, []);
  
  // Update zoom level
  const updateZoomLevel = useCallback((newZoom, options = {}) => {
    const { animate = true } = options;
    
    setZoomLevel(newZoom);
    
    if (mapRef.current && animate) {
      mapRef.current.setZoom(newZoom);
    }
  }, []);
  
  // Fit map to markers
  const fitToMarkers = useCallback((markers, options = {}) => {
    if (!mapRef.current || !markers || markers.length === 0) return;
    
    const { padding = 50 } = options;
    const bounds = calculateBounds(markers);
    
    if (bounds) {
      mapRef.current.fitBounds(bounds, padding);
    }
  }, []);
  
  // Center on specific marker
  const centerOnMarker = useCallback((marker, options = {}) => {
    const { 
      zoom = ROUTE_CONFIG.MAP.ZOOM_LEVELS.DETAIL,
      offset = 0
    } = options;
    
    const coords = extractCoordinates(marker);
    if (coords) {
      updateMapCenter(coords, { offset });
      updateZoomLevel(zoom);
    }
  }, [updateMapCenter, updateZoomLevel]);
  
  // Pan to location smoothly
  const panToLocation = useCallback((location) => {
    if (!mapRef.current) return;
    
    const coords = extractCoordinates(location);
    if (coords) {
      mapRef.current.panTo(coords);
    }
  }, []);
  
  // Get current map bounds
  const getMapBounds = useCallback(() => {
    if (!mapRef.current) return null;
    
    const bounds = mapRef.current.getBounds();
    if (!bounds) return null;
    
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    
    return {
      north: ne.lat(),
      east: ne.lng(),
      south: sw.lat(),
      west: sw.lng()
    };
  }, []);
  
  // Check if location is in current view
  const isInView = useCallback((location) => {
    if (!mapRef.current) return false;
    
    const bounds = mapRef.current.getBounds();
    if (!bounds) return false;
    
    const coords = extractCoordinates(location);
    if (!coords) return false;
    
    return bounds.contains(new window.google.maps.LatLng(coords.lat, coords.lng));
  }, []);
  
  // Reset map view
  const resetMapView = useCallback(() => {
    updateMapCenter(ROUTE_CONFIG.MAP.DEFAULT_CENTER);
    updateZoomLevel(ROUTE_CONFIG.MAP.DEFAULT_ZOOM);
  }, [updateMapCenter, updateZoomLevel]);
  
  // Handle map interactions
  const handleMapClick = useCallback((event) => {
    if (!event.latLng) return;
    
    return {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
  }, []);
  
  // Get map instance
  const getMapInstance = useCallback(() => mapRef.current, []);
  
  return {
    // State
    isLoaded,
    center,
    zoomLevel,
    mapRef,
    
    // Actions
    handleMapLoad,
    updateMapCenter,
    updateZoomLevel,
    fitToMarkers,
    centerOnMarker,
    panToLocation,
    getMapBounds,
    isInView,
    resetMapView,
    handleMapClick,
    getMapInstance
  };
};
